import {
  doc,
  collection,
  setDoc,
  updateDoc,
  deleteField,
  getDocs,
  getCountFromServer,
  onSnapshot,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { CHATTERS_ID } from '../../constants/game';

// Find a game by its chatterCode (separate from the player code) and register the user as a chatter.
// Works for both waiting and active games (chatters can join mid-stream).
export async function joinGameByCodeAsChatters(code, userId, username) {
  const q = query(
    collection(db, 'games'),
    where('chatterCode', '==', code.toUpperCase()),
    where('chattersEnabled', '==', true)
  );
  const snap = await getDocs(q);

  if (snap.empty) {
    throw new Error('Chat code not found. Make sure you have the correct chat code from the host.');
  }

  const gameDoc = snap.docs[0];
  const game = gameDoc.data();

  if (game.status === 'completed') {
    throw new Error('This game has already ended.');
  }

  // Check chatter count limit (allow up to 10,000 chatters)
  const chattersRef = collection(db, 'games', gameDoc.id, 'chatters');
  const countSnap = await getCountFromServer(chattersRef);
  if (countSnap.data().count >= 10000) {
    throw new Error('This game has reached the maximum number of chatters (10,000).');
  }

  await setDoc(doc(db, 'games', gameDoc.id, 'chatters', userId), {
    uid: userId,
    username,
    joinedAt: serverTimestamp(),
    // verifyCode is included so the Firestore security rule can confirm the user
    // knew the chatterCode at write time. It mirrors the value already readable on
    // the game document so no new information is exposed.
    verifyCode: code.toUpperCase(),
  });

  return gameDoc.id;
}

// Subscribe to the current user's own chatter profile for this game.
// Calls callback with the profile object, or null if not a chatter.
export function subscribeToChatterProfile(gameId, userId, callback) {
  return onSnapshot(doc(db, 'games', gameId, 'chatters', userId), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  });
}

// Subscribe to all chatter profiles joined to a game.
export function subscribeToChatterProfiles(gameId, callback) {
  return onSnapshot(collection(db, 'games', gameId, 'chatters'), (snap) => {
    const chatters = {};
    snap.forEach((d) => { chatters[d.id] = d.data(); });
    callback(chatters);
  });
}

// Submit a chatter's original song proposal. Sets proposedSong (immutable after this write)
// and initialises songVote to the same song. Chatters may only call this once per round.
// song: { title, artist, videoId? }
export async function proposeChatterSong(gameId, roundId, userId, song) {
  await setDoc(
    doc(db, 'games', gameId, 'rounds', roundId, 'chatterVotes', userId),
    { proposedSong: song, songVote: song, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

// Switch a chatter's vote to an existing proposal without changing their own proposal.
// Only updates songVote — proposedSong is never touched here.
// song: { title, artist, videoId? }
export async function submitChatterSongVote(gameId, roundId, userId, songVote) {
  await setDoc(
    doc(db, 'games', gameId, 'rounds', roundId, 'chatterVotes', userId),
    { songVote, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

// Lock in a chatter's score and predictions for the current round.
export async function lockInChatterScore(gameId, roundId, userId, { actualScore, predictions }) {
  await setDoc(
    doc(db, 'games', gameId, 'rounds', roundId, 'chatterVotes', userId),
    {
      actualScore,
      predictions,
      lockedIn: true,
      submittedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function unlockChatterScore(gameId, roundId, userId) {
  await updateDoc(
    doc(db, 'games', gameId, 'rounds', roundId, 'chatterVotes', userId),
    { lockedIn: false, submittedAt: deleteField() }
  );
}

// Subscribe to all chatter votes for the current round (used by host + chatters).
export function subscribeToChatterVotes(gameId, roundId, callback) {
  return onSnapshot(
    collection(db, 'games', gameId, 'rounds', roundId, 'chatterVotes'),
    (snap) => {
      const votes = {};
      snap.forEach((d) => { votes[d.id] = d.data(); });
      callback(votes);
    }
  );
}

// Aggregate all locked chatter votes and write to scores/__chatters__.
// regularPlayerIds: playerIds without CHATTERS_ID, used for averaging predictions.
// Returns true if at least one chatter vote was aggregated, false if none.
export async function aggregateChatterScores(gameId, roundId, chatterVotes, regularPlayerIds) {
  const lockedVotes = Object.values(chatterVotes).filter(
    (v) => v.lockedIn && v.actualScore != null
  );

  if (lockedVotes.length === 0) {
    // No chatter participation — write a null placeholder so the reveal can proceed
    await setDoc(doc(db, 'games', gameId, 'rounds', roundId, 'scores', CHATTERS_ID), {
      actualScore: null,
      predictions: {},
      lockedIn: true,
      isAggregated: true,
      chatterCount: 0,
      submittedAt: serverTimestamp(),
    });
    return false;
  }

  const avgScore = Math.round(
    (lockedVotes.reduce((sum, v) => sum + v.actualScore, 0) / lockedVotes.length) * 10
  ) / 10;

  const predictions = {};
  for (const playerId of regularPlayerIds) {
    const preds = lockedVotes
      .map((v) => v.predictions?.[playerId])
      .filter((p) => p != null);
    if (preds.length > 0) {
      predictions[playerId] = Math.round(
        (preds.reduce((sum, p) => sum + p, 0) / preds.length) * 10
      ) / 10;
    }
  }

  await setDoc(doc(db, 'games', gameId, 'rounds', roundId, 'scores', CHATTERS_ID), {
    actualScore: avgScore,
    predictions,
    lockedIn: true,
    isAggregated: true,
    chatterCount: lockedVotes.length,
    submittedAt: serverTimestamp(),
  });

  return true;
}

// Aggregate song poll votes into ranked proposals.
// Returns: [{ song: { title, artist, videoId? }, voters: [uid, ...], key: string }, ...]
// Proposals are derived from proposedSong (immutable per-chatter) so a chatter cannot
// alter a proposal that has already received votes. Vote counts are derived from songVote
// (which each chatter can switch freely to any existing proposal).
export function aggregateSongVotes(chatterVotes) {
  const proposals = {};

  // Build the list of proposals from each chatter's proposedSong.
  for (const vote of Object.values(chatterVotes)) {
    if (!vote.proposedSong) continue;
    const { title, artist, videoId } = vote.proposedSong;
    const key = videoId || `${title}||${artist}`;
    if (!proposals[key]) {
      proposals[key] = { song: vote.proposedSong, voters: [], key };
    }
  }

  // Count votes: each chatter's songVote points to whichever proposal they support.
  for (const [uid, vote] of Object.entries(chatterVotes)) {
    if (!vote.songVote) continue;
    const { title, artist, videoId } = vote.songVote;
    const key = videoId || `${title}||${artist}`;
    if (proposals[key]) {
      proposals[key].voters.push(uid);
    }
  }

  return Object.values(proposals).sort((a, b) => b.voters.length - a.voters.length);
}
